import { ReactElement, useMemo, useRef } from 'react';
import { BottomDrawerModel, MultiDateRangePicker } from '@components/common';
import { ISiteSummaryFilterInitialValue, ISiteSummaryFiltersProps } from '@components/siteSummary/components';
import { useFormik } from 'formik';
import { useOutsideDialogClose } from '@hooks/useOutsideDialogClose';
import { siteStatusLists } from '@config/constant';
import { FilterInputLabelCheckboxField } from '@components/common/filterFormControls/filterInputLabelCheckboxField';
import { useSiteSummaryListingStore } from '@store/siteSummaryListingFilterStore';
import { useComponentLevelAccessCheck } from '../../../../../src/authGuard/hooks/useComponentLevelAccessCheck';
import { useUserRoleType } from '@hooks/useUserRoleType';
import { RoleType } from '@helper/utils';
import Dropdown from '@components/common/formControl/dropdown/dropdown';
import { CMDIDropdownRBDMItem } from '@services/hooks/endusers/cmdiDropdownRBDM';
import { useGetRxDb } from '@hooks/getRxdbData';

export const SiteSummaryFilters = (props: ISiteSummaryFiltersProps): ReactElement => {
	const userRole = useUserRoleType();
	const { openFilters, closeFilters, isHideFTQFilter, isHideKAESSFilter } = props;
	const dialogRef = useRef<HTMLDivElement | null>(null);
	useOutsideDialogClose({ dialogRef, callBackFunction: closeFilters });
	const { updateFilter, resetSiteSummaryFilter, allSiteSummaryFilter } = useSiteSummaryListingStore();
	const checkKaessSitePermissions = useComponentLevelAccessCheck('lost-leads');
	const isSSMFilterEnable = useComponentLevelAccessCheck('ssm-filter-site-summary');
	const { result: mdiCodeOptions } = useGetRxDb<CMDIDropdownRBDMItem>('mdi_code_list');

	const formik = useFormik({
		initialValues: allSiteSummaryFilter,
		onSubmit: (values: ISiteSummaryFilterInitialValue): void => {
			updateFilter(values);
			closeFilters();
		},
	});

	const handleSubmit = (): void => {
		formik.handleSubmit();
	};

	const clearAll = (): void => {
		resetSiteSummaryFilter();
		closeFilters();
	};

	const isFilterChecked =
		formik.values.current_stage_of_site_c !== '' ||
		formik.values.lastactivitydate.every((item) => item?.toString().length) ||
		formik.values.competition_site ||
		formik.values.adopted_site ||
		formik.values.ssm_site ||
		(userRole === 'RBDM' && formik.values.mdicode && formik.values.mdicode.length > 0);

	const handleCheckboxChangeEvent = (e: React.ChangeEvent<HTMLInputElement>): void => {
		if (!e || !e.target) {
			return;
		}
		const name = e.target.name;
		const value = e?.target?.checked;
		formik.setFieldValue(name, value);
	};

	const handleLostSiteChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const isChecked = e.target.checked;
		formik.setFieldValue('lost_site', isChecked);
		// When Lost site is checked, set current_stage_of_site_c to "Rejected"
		if (isChecked) {
			formik.setFieldValue('current_stage_of_site_c', 'Rejected');
		} else {
			formik.setFieldValue('current_stage_of_site_c', '');
		}
	};

	const mdiCodeOptionsFormatted = useMemo(
		() => mdiCodeOptions.map((item) => ({ id: item.sfid, value: item.mdicode_c ?? '' })),
		[mdiCodeOptions]
	);

	return (
		<BottomDrawerModel openModelStatus={openFilters} modelId="filter-main-wrapper">
			<div className="container">
				<button
					type="button"
					aria-label="filter popup"
					className="filter-sort-icon icon-filter"
					onClick={closeFilters}
				></button>
				<div ref={dialogRef} className="filter-drawer-inner-wrapper filter">
					<span className="filter-title">
						<p className="h2">Filter</p>
						<button
							className="clear-all"
							type="button"
							hidden={!isFilterChecked}
							onClick={clearAll}
							aria-label="Clear All"
						>
							Clear All
						</button>
					</span>
					<form>
						<div className="form-fieldset row">
							{userRole === RoleType.RBDM && (
								<div className="d-col">
									<span className="form-title h4">
										<span className="icon-shield"></span>
										MDI Code
									</span>
									<Dropdown
										name="mdicode"
										id="mdicode"
										title={formik.values.mdicode ?? ''}
										options={mdiCodeOptionsFormatted}
										setFieldValue={formik.setFieldValue}
									/>
								</div>
							)}
							{!isHideFTQFilter && (
								<div className="d-col">
									<span className="form-title h4">
										<span className="icon-hammer"></span>Visit Status (FTQ)
									</span>
									<ul className="custom-check primary">
										{siteStatusLists.map((status) => (
											<li key={status.label} className="custom-check-item">
												<FilterInputLabelCheckboxField<ISiteSummaryFilterInitialValue>
													id={status.label}
													name={status.name}
													formik={formik}
													formikValue={formik.values.current_stage_of_site_c}
													value={status.value}
													label={status.label}
												/>
											</li>
										))}
									</ul>
								</div>
							)}

							<div className="d-col">
								<span className="form-title h4">
									<span className="icon-date"></span>Date of Visit
								</span>
								<MultiDateRangePicker
									id="lastactivitydate"
									name="lastactivitydate"
									value={formik.values.lastactivitydate}
									formik={formik}
									maxDate={new Date()}
								/>
							</div>
							{!isHideKAESSFilter && (
								<div className="d-col">
									<div className="sites-label">
										<span className="form-title h4">
											<span className="icon-user"> </span>Competition Sites
										</span>

										<ul className="custom-toggle">
											<li className="custom-toggle-item">
												<input
													type="checkbox"
													checked={formik.values.competition_site}
													name="competition_site"
													id="competition_site"
													onChange={handleCheckboxChangeEvent}
												/>
												<label htmlFor="competition_site">
													<span className="visually-hidden">competition_site</span>
												</label>
											</li>
										</ul>
									</div>
								</div>
							)}
							{!isHideKAESSFilter && (
								<div className="d-col">
									<div className="sites-label">
										<span className="form-title h4">
											<span className="icon-user"> </span>Adopted User Sites
										</span>

										<ul className="custom-toggle">
											<li className="custom-toggle-item">
												<input
													type="checkbox"
													id="adopted_user_sites"
													name="adopted_site"
													checked={formik.values.adopted_site}
													onChange={handleCheckboxChangeEvent}
												/>
												<label htmlFor="adopted_user_sites">
													<span className="visually-hidden">Adopted User Sites</span>
												</label>
											</li>
										</ul>
									</div>
								</div>
							)}
							{!checkKaessSitePermissions && !isHideKAESSFilter && (
								<div className="d-col">
									<div className="sites-label">
										<span className="form-title h4">
											<span className="icon-user"> </span>Lost Site
										</span>

										<ul className="custom-toggle">
											<li className="custom-toggle-item">
												<input
													type="checkbox"
													id="lost_site"
													name="lost_site"
													checked={formik.values.lost_site}
													onChange={handleLostSiteChange}
												/>
												<label htmlFor="lost_site">
													<span className="visually-hidden">Lost Site</span>
												</label>
											</li>
										</ul>
									</div>
								</div>
							)}
							{!isSSMFilterEnable && (
								<div className="d-col">
									<div className="sites-label">
										<span className="form-title h4">
											<span className="icon-user"> </span>SMM
										</span>

										<ul className="custom-toggle">
											<li className="custom-toggle-item">
												<input
													type="checkbox"
													id="ssm_site"
													name="ssm_site"
													checked={formik.values.ssm_site}
													onChange={handleCheckboxChangeEvent}
												/>
												<label htmlFor="ssm_site">
													<span className="visually-hidden">SSM</span>
												</label>
											</li>
										</ul>
									</div>
								</div>
							)}
						</div>
					</form>
					<div className="d-col submit">
						<button
							type="submit"
							disabled={!isFilterChecked}
							onClick={handleSubmit}
							className="btn btn-primary btn-medium"
						>
							Submit
						</button>
					</div>
				</div>
			</div>
		</BottomDrawerModel>
	);
};
